import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

/**
 * Orchestrator Agent Service
 * Single entrypoint you talk to; it decides which agents/commands to use.
 *
 * The backend is expected to expose POST /ai/orchestrator with the shape
 * described in BACKEND_ORCHESTRATOR_GUIDE.md.
 */
export const OrchestratorAgentService = {
  /**
   * Send a high-level instruction to the orchestrator agent.
   * Frontend is the central brain that provides app context; backend
   * responds with a natural language reply plus an encoded list of commands
   * that represent user-level actions ("functions").
   *
   * @param {Object} payload
   * @param {string} payload.userId
   * @param {string} [payload.sessionId]
   * @param {string} payload.message - User’s natural language instruction.
   * @param {Object} [payload.appContext] - Current app state snapshot
   *   (current page, selected task, etc.).
   * @returns {Promise<{ reply: string, commands: Array, raw: any }>}
   */
  async sendInstruction(payload) {
    const body = {
      agentId: 'orchestrator',
      persona: payload.persona || null,
      userId: payload.userId,
      sessionId: payload.sessionId || null,
      message: payload.message,
      appContext: payload.appContext || {},
    };

    const response = await fetch(`${API_BASE_URL}/ai/orchestrator`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Orchestrator API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Normalize the response so the rest of the app always gets
    // a plain text reply and a structured list of commands.
    const reply = data.reply || data.message || data.text || '';
    const commands = Array.isArray(data.commands) ? data.commands : [];

    return {
      reply,
      commands,
      raw: data,
    };
  },
};

export default OrchestratorAgentService;
